<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row align="center" class="mb-2">
        <v-col cols="12" md="8">
          <div class="text-h5 font-weight-bold">
            Incorporation &amp; Corporate Matter
          </div>
          <div class="caption grey--text">
            รายการข้อมูลนิติบุคคล / บุคคลธรรมดา
          </div>
        </v-col>
        <v-col cols="12" md="4" class="d-flex justify-end">
          <v-btn
            color="#0A7EA5"
            rounded
            dark
            @click="goToNewMatter"
            v-if="authorize_add"
          >
            <v-icon left>mdi-plus</v-icon>New Matter Data Sheet
          </v-btn>
        </v-col>
      </v-row>

      <!-- Search Row -->
      <v-row class="mt-2" align="center">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            placeholder="Search (e.g. Client, Reg No., Nationality)"
            outlined
            dense
            hide-details
            clearable
            @keyup.enter="onSearchPagination"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="search_entity_type"
            :items="entity_type_list"
            label="Entity Type | ประเภทลูกค้า"
            item-text="name"
            item-value="value"
            outlined
            dense
            hide-details
            clearable
            @change="onSearchPagination"
          ></v-select>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            v-model="search_status"
            :items="status_list"
            label="Status"
            item-text="name"
            item-value="value"
            outlined
            dense
            hide-details
            clearable
            @change="onSearchPagination"
          ></v-select>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn outlined @click="onSearchPagination">
            <v-icon left>mdi-magnify</v-icon>Search
          </v-btn>
        </v-col>
      </v-row>
      <div class="caption red--text mb-2">*input text and enter for search</div>

      <!-- Table -->
      <v-card outlined tile>
        <div class="d-flex align-center px-2 pt-2">
          <v-select
            v-model="itemsPerPage"
            @change="onSearchPagination"
            style="width:150px"
            :items="item_per_page_list"
            label="Items per page"
            dense
            outlined
            hide-details
          ></v-select>
        </div>
        <div class="text-center pt-2">
          <v-pagination
            class="ma-3"
            v-model="page"
            :length="pageCount"
            @input="loadDataByPage"
            total-visible="19"
          ></v-pagination>
        </div>

        <v-data-table
          :headers="headers"
          :items="mDataArray2"
          :server-items-length="totalItems"
          hide-default-footer
          class="elevation-1"
          :loading="tableLoading"
          loading-text="Loading... Please wait"
        >
          <template v-slot:progress>
            <div class="text-center pa-6">
              <v-progress-circular
                indeterminate
                color="primary"
                size="50"
                width="8"
              ></v-progress-circular>
              <div class="mt-3 text-subtitle-1 primary--text">
                กำลังโหลดข้อมูล...
              </div>
            </div>
          </template>

          <!-- Client Name -->
          <template v-slot:[`item.client_name`]="{ item }">
            <div>
              <strong>{{ item.client_name }}</strong>
            </div>
            <div v-if="item.client_name_en" class="caption grey--text">
              {{ item.client_name_en }}
            </div>
          </template>

          <!-- Entity Type badge -->
          <template v-slot:[`item.entity_type`]="{ item }">
            <v-chip
              :color="getEntityTypeColor(item.entity_type)"
              small
              label
              class="font-weight-medium"
            >
              {{ item.entity_type }}
            </v-chip>
          </template>

          <!-- Status badge -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="item.status === 'Active' ? 'success' : 'grey lighten-1'"
              :text-color="item.status === 'Active' ? 'white' : 'grey darken-2'"
              small
              label
            >
              <v-icon left x-small>mdi-circle</v-icon>{{ item.status }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn outlined x-small @click="viewItem(item.id)">View</v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Delete Dialog -->
      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5"
            >Are you sure you want to delete this item?</v-card-title
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeDelete"
              >Cancel</v-btn
            >
            <v-btn color="blue darken-1" text @click="deleteItemConfirm"
              >OK</v-btn
            >
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <SuccessDialog
        :status="dialogAdd"
        :text_color="text_color"
        :title="title"
        :message="message"
      />
      <SuccessDialogPush
        :status="dialogAddPush"
        :text_color="text_color"
        :title="title"
        :message="message"
        :link="link"
      />
    </v-card>
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import { server } from "@/services/constants";
import {
  getIncorporateMatterPagination,
  deleteIncorporateMatter,
} from "@/services/api-dtime/api_incorporate";

export default {
  name: "incorporate-matter-list",
  components: { SuccessDialog, SuccessDialogPush },
  data() {
    return {
      tableLoading: false,
      item_per_page_list: [10, 20, 100, 200, 500, 1000],
      page: 1,
      pageCount: 0,
      itemsPerPage: 100,
      totalItems: 0,
      mDataArray2: [],

      search: "",
      search_entity_type: "",
      search_status: "",

      entity_type_list: [
        { name: "Limited Company", value: "Limited Company" },
        { name: "Public Company Limited", value: "Public Company Limited" },
        { name: "Foundation", value: "Foundation" },
        { name: "Representative Office", value: "Representative Office" },
        { name: "Individual", value: "Individual" },
      ],
      status_list: [
        { name: "Active", value: "Active" },
        { name: "Inactive", value: "Inactive" },
      ],

      dialogDelete: false,
      dialogAdd: false,
      dialogAddPush: false,
      text_color: "text-h5 green--text text-center",
      title: "",
      message: "",
      link: "",
      id_del: 0,

      authorize_view: false,
      authorize_add: false,
      authorize_edit: false,
      authorize_del: false,
      authorize_id: "",
      userId: "",

      headers: [
        {
          text: "CLIENT NAME | ชื่อลูกค้า",
          value: "client_name",
          class: "bg-colorth tabletextwhite",
          divider: true,
        },
        {
          text: "ENTITY TYPE | ประเภทลูกค้า",
          value: "entity_type",
          class: "bg-colorth tabletextwhite",
          divider: true,
        },
        {
          text: "NATIONALITY | สัญชาติ",
          value: "nationality",
          class: "bg-colorth tabletextwhite",
          divider: true,
        },
        {
          text: "REGISTRATION NO. | เลขทะเบียน",
          value: "registration_no",
          class: "bg-colorth tabletextwhite",
          divider: true,
        },
        {
          text: "STATUS",
          value: "status",
          class: "bg-colorth tabletextwhite",
          align: "center",
          divider: true,
          width: "120px",
        },
        {
          text: "ACTION",
          value: "actions",
          class: "bg-colorth tabletextwhite",
          align: "center",
          sortable: false,
          divider: true,
          width: "90px",
        },
      ],
    };
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    this.$hideLoader();
    this.userId = localStorage.getItem(server.USER_ID);
    this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

    if (!this.authorize_id || this.authorize_id == 0) {
      this.setupAlertDialog(
        true,
        "Authorize Failed",
        "Please Logout And Login Again",
        "text-h5 red--text text-center",
      );
      this.$router.back();
      return;
    }

    // Authorize check
    let parser = document.createElement("a");
    parser.href = window.location;
    const router_path = parser.pathname.replace("/", "");
    const { default: api } = await import("@/services/api");
    const res_auth = await api.getAuthorize(this.userId, router_path);
    this.authorize_view = res_auth.data[0].smd_view >= 1;
    this.authorize_add = res_auth.data[0].smd_add >= 1;
    this.authorize_edit = res_auth.data[0].smd_edit >= 1;
    this.authorize_del = res_auth.data[0].smd_del >= 1;

    if (!this.authorize_view) {
      this.$router.back();
      return;
    }

    await this.onSearchPagination();
  },
  methods: {
    getEntityTypeColor(type) {
      const map = {
        "Limited Company": "blue lighten-4",
        "Public Company Limited": "pink lighten-4",
        Foundation: "amber lighten-4",
        "Representative Office": "deep-purple lighten-4",
        Individual: "green lighten-4",
      };
      return map[type] || "grey lighten-3";
    },

    goToNewMatter() {
      this.$router.push("/incorporate-matter-entity-select");
    },

    viewItem(id) {
      this.$router.push(`/incorporate-matter-view/${id}`);
    },

    async loadDataByPage(pageNumber) {
      this.tableLoading = true;
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getIncorporateMatterPagination(
          pageNumber,
          this.itemsPerPage,
          "id",
          "DESC",
          company_id,
          this.search,
          this.search_entity_type,
          this.search_status,
        );
        this.mDataArray2 = res.data.data;
        this.totalItems = res.data.pagination.totalRecords;
        this.page = res.data.pagination.currentPage;
        this.pageCount = res.data.pagination.totalPages;
      } catch (error) {
        console.error("Error loading page:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถโหลดข้อมูลได้",
          "text-h5 red--text text-center",
        );
      } finally {
        this.tableLoading = false;
      }
    },

    async onSearchPagination() {
      this.page = 1;
      this.tableLoading = true;
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getIncorporateMatterPagination(
          1,
          this.itemsPerPage,
          "id",
          "DESC",
          company_id,
          this.search,
          this.search_entity_type,
          this.search_status,
        );
        this.mDataArray2 = res.data.data;
        this.totalItems = res.data.pagination.totalRecords;
        this.pageCount = res.data.pagination.totalPages;
      } catch (error) {
        console.error("Error searching:", error);
        this.mDataArray2 = [];
        this.totalItems = 0;
        this.pageCount = 0;
      } finally {
        this.tableLoading = false;
      }
    },

    deleteItem(id) {
      this.id_del = id;
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      try {
        await deleteIncorporateMatter(this.id_del);
        this.setupAlertDialog(
          true,
          "Success",
          "ลบข้อมูลสำเร็จ",
          "text-h5 green--text text-center",
        );
        await this.onSearchPagination();
      } catch (error) {
        console.error("Error deleting:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถลบข้อมูลได้",
          "text-h5 red--text text-center",
        );
      }
      this.closeDelete();
    },

    closeDelete() {
      this.dialogDelete = false;
      this.id_del = 0;
    },

    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
  },
};
</script>

<style scoped>
.bg-colorth {
  background-color: #8d8d8d !important;
}
.tabletextwhite {
  color: white !important;
}
</style>
